import { isArchitectRole, isDesignPartnerOrEngineerRole } from './userRoles';
import type { EmployeeDesignation, ExecutiveBriefingProfile, UserRole } from '../types';
import { filterBellNotificationsForUser } from './eaProjectBellPolicy';
import { filterDealWonBellsForUser, type DealWonNotifyViewer } from './dealWonNotify';
import { canReviewDesignerInvoices } from './projectPLAccess';

/**
 * Bells a designer / engineer should see.
 * Sales, hiring, billing, SMS, and deal-won dollars stay off this list.
 * External architects use {@link ARCHITECT_PARTNER_BELL_TYPES} (no portal/sales blasts).
 */
export const PORTAL_PARTNER_BELL_TYPES = new Set([
  'task_assigned',
  'ext_task_assigned',
  'task_comment',
  'architect_comment',
  'external_comment',
  'task_mention',
  'mention',
  'board_mention',
  'board_comment',
  'pdf_comment',
  'pdf_external_comment',
  'plan_review_complete',
  'project_file_version',
  'portal_feedback',
  'portal_upload',
  'portal_annotation',
  'portal_deliverable_approval',
  'portal_walkthrough_summary',
  'questionnaire_completed',
  'ext_status_changed',
  'ext_action_required',
  'ext_task_completed',
  'onboarding_action_needed',
  'task_reminder',
  'proposal_comment',
  'architect_standup_report',
]);

/**
 * External architects only see design-work bells on assigned projects —
 * discussion comments/mentions, plan review, and file versions — not portal
 * approvals, questionnaire, deal-won, hiring, or billing blasts.
 */
export const ARCHITECT_PARTNER_BELL_TYPES = new Set([
  'task_assigned',
  'ext_task_assigned',
  'task_comment',
  'architect_comment',
  'external_comment',
  'task_mention',
  'mention',
  'board_mention',
  'board_comment',
  'pdf_comment',
  'pdf_external_comment',
  'plan_review_complete',
  'project_file_version',
  'ext_status_changed',
  'ext_action_required',
  'ext_task_completed',
  'task_reminder',
  'architect_standup_report',
]);

export type BellViewer = DealWonNotifyViewer & { role?: string | null };

export function isPortalPartnerRelevantBell(type: string | undefined): boolean {
  return PORTAL_PARTNER_BELL_TYPES.has(String(type || ''));
}

export function isArchitectRelevantBell(type: string | undefined): boolean {
  return ARCHITECT_PARTNER_BELL_TYPES.has(String(type || ''));
}

function viewerCanReviewDesignerInvoices(user: BellViewer | null | undefined): boolean {
  const designations = Array.isArray(user?.designations)
    ? (user!.designations as EmployeeDesignation[])
    : undefined;
  const briefing = user?.executiveBriefing;
  const briefingProfile = (briefing?.enabled ? briefing.profile : briefing?.profile) as
    | ExecutiveBriefingProfile
    | null
    | undefined;
  return canReviewDesignerInvoices({
    authRole: user?.role ?? undefined,
    designations,
    briefingProfile: briefingProfile ?? null,
  });
}

export function filterBellsForViewer<T extends { type?: string; projectId?: string | null; opsTaskId?: string | null }>(
  items: T[],
  user: BellViewer | null | undefined,
): T[] {
  const afterEa = filterBellNotificationsForUser(items, user?.designations);
  const afterDeal = filterDealWonBellsForUser(afterEa, user);
  const afterDesignerInvoice = afterDeal.filter((n) => {
    if (n.type !== 'designer_invoice_review') return true;
    return viewerCanReviewDesignerInvoices(user);
  });
  const role = user?.role as UserRole | undefined;
  if (isArchitectRole(role)) {
    return afterDesignerInvoice.filter((n) => isArchitectRelevantBell(n.type));
  }
  if (!isDesignPartnerOrEngineerRole(role)) return afterDesignerInvoice;
  return afterDesignerInvoice.filter((n) => isPortalPartnerRelevantBell(n.type));
}
